import { useState } from "react";
import { Clock, Mail, MapPin, MapPinned, MessageCircle, Phone, Smartphone } from "lucide-react";
import { Seo } from "@/components/site/Seo";
import { Hero } from "@/components/site/Hero";
import { PageShell } from "@/components/site/PageShell";
import { ServiceArea } from "@/components/sections/Sections";
import { breadcrumbLd, businessLd } from "@/lib/jsonld";
import { SITE } from "@/lib/site";

const crumbs = [{ label: "Kontakt & Anfahrt", to: "/kontakt" }];
const { lat, lon } = SITE.geo;
const MAP_SRC = `https://www.openstreetmap.org/export/embed.html?bbox=${lon - 0.008}%2C${lat - 0.004}%2C${lon + 0.008}%2C${lat + 0.004}&layer=mapnik&marker=${lat}%2C${lon}`;

const CARDS = [
  { icon: MapPin, label: "Adresse", value: `${SITE.street}, ${SITE.zip} ${SITE.city}`, testId: "contact-address" },
  { icon: Phone, label: "Festnetz (24 h)", value: SITE.phoneIntl, href: SITE.phoneHref, testId: "contact-phone" },
  { icon: Smartphone, label: "Mobil", value: SITE.mobileIntl, href: SITE.mobileHref, testId: "contact-mobile" },
  { icon: MessageCircle, label: "WhatsApp", value: "Nachricht schreiben", href: SITE.whatsappHref, external: true, testId: "contact-whatsapp" },
  { icon: Mail, label: "E-Mail", value: SITE.email, href: `mailto:${SITE.email}`, testId: "contact-email" },
  { icon: Clock, label: "Erreichbarkeit", value: SITE.hours.map((h) => `${h.days}: ${h.time}`).join(" · "), testId: "contact-hours" },
];

const MapConsent = () => {
  const [show, setShow] = useState(false);
  if (show) {
    return <iframe title="Karte: Kfz-Sachverständiger Marcone, Neusser Str. 401–403, Köln" src={MAP_SRC} className="h-80 w-full rounded-2xl border-0" loading="lazy" data-testid="contact-map-iframe" />;
  }
  return (
    <div className="grid h-80 place-items-center rounded-2xl bg-brand-mist p-6 text-center" data-testid="contact-map-placeholder">
      <div className="max-w-sm">
        <MapPinned className="mx-auto h-9 w-9 text-brand-blue" strokeWidth={1.75} />
        <p className="mt-3 font-bold text-brand-anthracite">Karte von OpenStreetMap laden?</p>
        <p className="mt-1 text-sm">Beim Laden werden Daten (z. B. Ihre IP-Adresse) an die OpenStreetMap Foundation übertragen.</p>
        <button type="button" onClick={() => setShow(true)} data-testid="contact-map-load"
          className="mt-4 rounded-full bg-brand-blue px-5 py-2.5 text-sm font-bold text-white transition-colors hover:bg-brand-blue-dark">
          Karte laden
        </button>
      </div>
    </div>
  );
};

export default function ContactPage() {
  return (
    <>
      <Seo
        title="Kontakt & Anfahrt – Kfz-Gutachter Köln-Nippes | Marcone"
        description="Kontakt zum Kfz-Sachverständigen Francesco Marcone: Neusser Str. 401–403, 50733 Köln. Mo–Fr 24 h erreichbar – per Telefon, WhatsApp, E-Mail oder Formular."
        path="/kontakt"
        jsonLd={[businessLd(), breadcrumbLd(crumbs)]}
      />
      <PageShell
        hero={<Hero image="real-buero-beratung" imagePosition="center 30%" imageAlt="Persönliche Beratung im Sachverständigenbüro Marcone in Köln-Nippes" eyebrow="Kontakt" title="Kontakt & Anfahrt" subtitle="Rufen Sie an, schreiben Sie uns oder besuchen Sie uns in Köln-Nippes." breadcrumbs={crumbs} badges={false} />}
      >
        <div className="space-y-14">
          <section>
            <h2 className="h2-brand mb-6 text-2xl sm:text-3xl">So erreichen Sie uns</h2>
            <ul className="grid gap-3 sm:grid-cols-2">
              {CARDS.map(({ icon: Icon, label, value, href, external, testId }) => (
                <li key={label} className="flex gap-4 rounded-2xl border border-slate-200 p-5">
                  <span className="grid h-11 w-11 shrink-0 place-items-center rounded-xl bg-brand-yellow text-brand-anthracite"><Icon className="h-5 w-5" /></span>
                  <span className="min-w-0">
                    <span className="block text-xs font-bold uppercase tracking-wider text-brand-blue">{label}</span>
                    {href ? (
                      <a href={href} target={external ? "_blank" : undefined} rel={external ? "noopener noreferrer" : undefined} className="block break-words font-semibold text-brand-anthracite hover:text-brand-blue" data-testid={testId}>{value}</a>
                    ) : (
                      <span className="block font-semibold text-brand-anthracite" data-testid={testId}>{value}</span>
                    )}
                  </span>
                </li>
              ))}
            </ul>
          </section>
          <section>
            <h2 className="h2-brand mb-3 text-2xl sm:text-3xl">Anfahrt</h2>
            <p className="mb-6 leading-relaxed">
              Unser Büro befindet sich an der Neusser Straße in Köln-Nippes und ist gut mit den Stadtbahnlinien entlang der Neusser Straße erreichbar.
              Noch einfacher: Wir kommen zu Ihnen – nach Hause, an den Arbeitsplatz oder in die Werkstatt.
            </p>
            <MapConsent />
          </section>
          <section>
            <h2 className="h2-brand mb-6 text-2xl sm:text-3xl">Einsatzgebiet</h2>
            <ServiceArea />
          </section>
        </div>
      </PageShell>
    </>
  );
}
